import type { ReactNode } from "react";
import { EXPANDABLE_DIALOG_WITH_SIDEBAR_SECTION_NAME } from "./constants";
import { LucideIcon } from "lucide-react";

export interface SectionProps {
  title: string;
  children?: ReactNode;
  icon?: LucideIcon;
  sectionKey?: string;
  trailingText?: string;
  actions?: Array<{
    icon: LucideIcon;
    onClick: () => void;
    label?: string;
    disabled?: boolean;
  }>;
  scrollable?: boolean;
  /**
   * Custom component to render in the sidebar instead of the default button.
   * When provided, this section will not be selectable and will not show content.
   * The custom component will be rendered in place of the sidebar menu button.
   */
  customSidebarComponent?: ReactNode;
  className?: string;
}

/**
 * Defines a section within an ExpandableDialogWithSidebar.
 *
 * This component renders the section's content and provides metadata (title, icon, key)
 * used by the parent to generate sidebar navigation. The section's key is used for
 * selection - if not provided, the title is used as the key.
 *
 * Sections can contain subsections by nesting Subsection components within their children.
 * When a section is selected, only its direct content is shown (Subsection components are
 * filtered out). If a section contains ONLY subsections with no other content, the first
 * subsection is automatically selected.
 *
 * @param title - Display name shown in the sidebar and section header
 * @param children - React content to display when this section is active (may include Subsection components)
 * @param icon - Lucide icon component to display in the sidebar menu
 * @param key - Optional unique identifier for the section (defaults to title if not provided)
 * @param trailingText - Optional text to display after breadcrumbs in the section header
 * @param actions - Optional array of action buttons to display in the section header (icon buttons)
 * @param customSidebarComponent - Custom component to render in the sidebar (e.g., button, link). When provided, the section is not selectable and shows no content.
 *
 * @example
 * ```tsx
 * <ExpandableDialogWithSidebar.Section
 *   title="Profile"
 *   icon={UserIcon}
 *   trailingText="(Active)"
 *   actions={[{icon: SettingsIcon, onClick: () => openSettings()}]}
 * >
 *   <div>User profile content</div>
 * </ExpandableDialogWithSidebar.Section>
 * ```
 *
 * @example
 * ```tsx
 * // Section with subsections
 * <ExpandableDialogWithSidebar.Section
 *   title="Contact"
 *   icon={UserIcon}
 *   actions={[{icon: ChevronRight, onClick: () => setSection('contact.details')}]}
 * >
 *   <div>Contact overview</div>
 *   <ExpandableDialogWithSidebar.Subsection title="Details" icon={InfoIcon}>
 *     <div>Detailed information</div>
 *   </ExpandableDialogWithSidebar.Subsection>
 * </ExpandableDialogWithSidebar.Section>
 * ```
 *
 * @example
 * ```tsx
 * // Section with custom sidebar component (e.g., link)
 * <ExpandableDialogWithSidebar.Section
 *   title="Help"
 *   customSidebarComponent={
 *     <a href="https://help.example.com" target="_blank">
 *       <SidebarMenuButton>
 *         <HelpCircleIcon />
 *         <span>Help Center</span>
 *       </SidebarMenuButton>
 *     </a>
 *   }
 * />
 * ```
 */
export const Section = ({ children }: SectionProps) => {
  return <>{children}</>;
};
Section.displayName = EXPANDABLE_DIALOG_WITH_SIDEBAR_SECTION_NAME;
